
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { NetworkDevice, Link, NodeType, PingResult, SensorModel } from './types';
import DeviceNode from './components/DeviceNode';
import { simulatePing, isValidIP, findPath } from './utils/networkLogic';

const App: React.FC = () => {
  const [devices, setDevices] = useState<NetworkDevice[]>([]);
  const [links, setLinks] = useState<Link[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [linkingFrom, setLinkingFrom] = useState<string | null>(null);
  const [pingIp, setPingIp] = useState('');
  const [pingLog, setPingLog] = useState<string[]>([]);
  const [activePath, setActivePath] = useState<string[]>([]);
  const [showCertificate, setShowCertificate] = useState(false);
  const [userName, setUserName] = useState('');
  const [userClass, setUserClass] = useState('');
  const [zoom, setZoom] = useState(1);
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  
  const CLOUD_SECRET_KEY = "IOT-SMP8-GATEWAY";
  const LOGO_URL = "https://cdn-icons-png.flaticon.com/512/2540/2540201.png";

  const sensorModels: SensorModel[] = ['DHT11', 'PIR', 'HC-SR04', 'MQ-135', 'LDR', 'BMP280'];

  const deviceCounters = useRef<Record<string, number>>({
    DASHBOARD: 0,
    SENSOR: 0,
    CONTROLLER: 0,
    CLOUD: 0,
    EXTENDER: 0
  });

  const [missionProgress, setMissionProgress] = useState({
    misi1: false, // API Key Auth
    misi2: false, // Chain WiFi
    misi3: false  // Online Access
  });

  const panStart = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const controller = devices.find(d => d.type === 'CONTROLLER');
    const cloud = devices.find(d => d.id === 'cloud-master');

    if (controller && cloud) {
      const isApiValidated = links.some(l => 
        ((l.from === controller.id && l.to === cloud.id) || (l.to === controller.id && l.from === cloud.id)) && l.isValidated
      );
      if (isApiValidated && !missionProgress.misi1) {
        setMissionProgress(prev => ({ ...prev, misi1: true }));
        setPingLog(prev => [`[Auth] API Key Berhasil! ESP32 terdaftar.`, ...prev.slice(0, 9)]);
      }
    }

    if (controller) {
      const isAnyClientConnected = devices.some(d => (d.type === 'DASHBOARD' || d.type === 'SENSOR') && findPath(d.id, controller.id, devices, links));
      if (isAnyClientConnected && !missionProgress.misi2) {
        setMissionProgress(prev => ({ ...prev, misi2: true }));
      }
    }
  }, [links, devices, missionProgress]);

  useEffect(() => {
    if (devices.length === 0) {
      setDevices([{
        id: 'cloud-master',
        type: 'CLOUD',
        name: 'IoT-CLOUD-SERVER',
        x: 80,
        y: 80,
        ip: '10.10.10.1',
        apiKey: CLOUD_SECRET_KEY,
        subnet: '255.255.255.0'
      }]);
    }
  }, []);

  const addDevice = (type: NodeType) => {
    if (type === 'CLOUD') return;
    deviceCounters.current[type] += 1;
    const currentNumber = deviceCounters.current[type];
    const id = Math.random().toString(36).substr(2, 9);
    
    let sensorModel: SensorModel | undefined;
    if (type === 'SENSOR') {
      sensorModel = sensorModels[Math.floor(Math.random() * sensorModels.length)];
    }

    const newDevice: NetworkDevice = {
      id,
      type,
      sensorModel,
      name: type === 'CONTROLLER' ? 'ESP32-Gateway' : 
            (type === 'SENSOR' ? `${sensorModel}-Sensor` : 
            (type === 'EXTENDER' ? `WiFi-Repeater` : `Client-Smartphone`)),
      x: (window.innerWidth / 2 - canvasOffset.x - 40) / zoom,
      y: (window.innerHeight / 2 - canvasOffset.y - 40) / zoom,
      ip: '',
      ipWan: '',
      apiKey: '',
      subnet: '255.255.255.0',
      gateway: '',
      firewallDisabled: true
    };
    
    setDevices(prev => [...prev, newDevice]);
    setSelectedId(id);
    setPingLog(prev => [`[System] Memasang ${newDevice.name}...`, ...prev.slice(0, 9)]);
  };

  const deleteDevice = (id: string) => {
    if (id === 'cloud-master') return;
    setDevices(prev => prev.filter(d => d.id !== id));
    setLinks(prev => prev.filter(l => l.from !== id && l.to !== id));
    setSelectedId(null);
    setPingLog(prev => [`[System] Perangkat dicabut.`, ...prev.slice(0, 9)]);
  };

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsPanning(true);
      panStart.current = { x: e.clientX - canvasOffset.x, y: e.clientY - canvasOffset.y };
      setSelectedId(null);
    }
  };

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleDeviceMouseDown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (linkingFrom) {
      if (linkingFrom !== id) {
        const fromDev = devices.find(d => d.id === linkingFrom);
        const toDev = devices.find(d => d.id === id);
        
        const isCloudLink = (fromDev?.type === 'CLOUD' || toDev?.type === 'CLOUD');
        const isGatewayLink = (fromDev?.type === 'CONTROLLER' || toDev?.type === 'CONTROLLER');

        if (isCloudLink) {
          if (!isGatewayLink) {
            setPingLog(prev => [`[Error] Cloud hanya bisa ke ESP32!`, ...prev.slice(0, 9)]);
            setLinkingFrom(null);
            return;
          }
          const gateway = fromDev?.type === 'CONTROLLER' ? fromDev : toDev;
          if (gateway?.apiKey !== CLOUD_SECRET_KEY) {
            setPingLog(prev => [`[Auth] Gagal: API Key ESP32 salah!`, ...prev.slice(0, 9)]);
            setLinkingFrom(null);
            return;
          }
        }

        const alreadyLinked = links.some(l => (l.from === linkingFrom && l.to === id) || (l.from === id && l.to === linkingFrom));
        if (!alreadyLinked) {
          setLinks(prev => [...prev, { 
            id: `link-${Date.now()}`, 
            from: linkingFrom, 
            to: id,
            isValidated: isCloudLink 
          }]);
          setPingLog(prev => [`[OK] Jalur tersambung.`, ...prev.slice(0, 9)]);
        }
      }
      setLinkingFrom(null);
      return;
    }
    const device = devices.find(d => d.id === id);
    if (device) {
      setDraggingId(id);
      setSelectedId(id);
      dragOffset.current = { x: e.clientX - (device.x * zoom + canvasOffset.x), y: e.clientY - (device.y * zoom + canvasOffset.y) };
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (draggingId) {
      setDevices(prev => prev.map(d => 
        d.id === draggingId ? { 
          ...d, 
          x: (e.clientX - dragOffset.current.x - canvasOffset.x) / zoom, 
          y: (e.clientY - dragOffset.current.y - canvasOffset.y) / zoom 
        } : d
      ));
    } else if (isPanning) {
      setCanvasOffset({
        x: e.clientX - panStart.current.x,
        y: e.clientY - panStart.current.y
      });
    }
  };

  const handleMouseUp = () => {
    setDraggingId(null);
    setIsPanning(false);
  };

  const handlePing = () => {
    if (!selectedId || !pingIp) return;
    const result = simulatePing(selectedId, pingIp, devices, links);
    setPingLog(prev => [`[Ping] Ke ${pingIp}: ${result.message}`, ...prev.slice(0, 8)]);
    if (result.success) {
      setActivePath(result.path);
      if (pingIp === '10.10.10.1') {
        setMissionProgress(prev => ({ ...prev, misi3: true }));
        setTimeout(() => setShowCertificate(true), 1500);
      }
      setTimeout(() => setActivePath([]), 2000);
    }
  };

  const updateDeviceConfig = (id: string, updates: Partial<NetworkDevice>) => {
    setDevices(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
  };

  const selectedDevice = devices.find(d => d.id === selectedId);

  return (
    <div className="flex flex-col h-screen overflow-hidden text-slate-900 bg-slate-50 font-sans">
      {showCertificate && (
        <div className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white max-w-4xl w-full rounded-[40px] shadow-2xl overflow-hidden flex flex-col md:flex-row border-8 border-white animate-in zoom-in-95 duration-300">
            <div className="w-full md:w-80 bg-slate-50 p-8 flex flex-col justify-center border-r border-slate-100">
              <h2 className="text-2xl font-black text-slate-800 leading-tight mb-2">Hebat!<br/><span className="text-indigo-600">IoT Expert.</span></h2>
              <div className="space-y-4 mb-8">
                <input className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 outline-none focus:border-indigo-500 font-bold" placeholder="Nama Kamu" value={userName} onChange={(e) => setUserName(e.target.value)} />
                <input className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 outline-none focus:border-indigo-500 font-bold" placeholder="Kelas" value={userClass} onChange={(e) => setUserClass(e.target.value)} />
              </div>
              <button onClick={() => window.print()} className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg active:scale-95 transition-transform">Cetak Sertifikat</button>
              <button onClick={() => setShowCertificate(false)} className="w-full py-4 mt-2 bg-white text-slate-400 rounded-2xl font-black text-xs uppercase tracking-widest border border-slate-100">Lanjut</button>
            </div>
            <div className="flex-1 bg-white p-12 relative flex flex-col items-center justify-center text-center">
               <img src={LOGO_URL} alt="Logo" className="w-20 h-20 mb-6 rounded-2xl shadow-lg" />
               <h4 className="text-3xl font-black text-slate-800 mb-8 uppercase tracking-tighter">IoT System Architect</h4>
               <p className="text-3xl font-black text-slate-800 border-b-4 border-indigo-500 inline-block px-4 pb-1">{userName || "Nama Siswa"}</p>
               <p className="text-sm font-bold text-slate-500 mt-4 italic">Membangun Jaringan IoT dengan ESP32 dan Berbagai Sensor</p>
               <p className="text-xs font-bold text-slate-400 mt-1 uppercase tracking-widest">SMP KELAS 8 - LAB KOMPUTER</p>
            </div>
          </div>
        </div>
      )}

      <header className="bg-white border-b px-6 py-2 flex items-center justify-between shadow-sm z-30">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Logo" className="w-8 h-8 object-contain" />
          <div>
            <h1 className="text-sm font-black uppercase tracking-tight text-slate-800 leading-none">IoT Junior Lab v2</h1>
            <p className="text-[8px] text-slate-400 font-bold uppercase tracking-widest leading-none mt-1">Skenario: Multi-Sensor Hybrid Networking</p>
          </div>
        </div>
        <div className="flex gap-2">
          {['DASHBOARD', 'SENSOR', 'CONTROLLER', 'EXTENDER'].map((type) => (
            <button key={type} onClick={() => addDevice(type as NodeType)} className="px-3 py-1.5 bg-white border-2 border-slate-100 rounded-lg hover:border-indigo-500 hover:text-indigo-600 transition-all text-[10px] font-black uppercase tracking-widest shadow-sm active:scale-95 text-slate-600">
              + {type === 'CONTROLLER' ? 'ESP32' : (type === 'DASHBOARD' ? 'HP' : (type === 'EXTENDER' ? 'EXTENDER' : 'SENSOR'))}
            </button>
          ))}
        </div>
      </header>

      <div className="flex flex-1 relative overflow-hidden">
        <div className="flex flex-col flex-1 relative overflow-hidden">
          <main 
            className={`flex-1 relative canvas-grid bg-white overflow-hidden cursor-${isPanning ? 'grabbing' : 'grab'}`} 
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleMouseMove} 
            onMouseUp={handleMouseUp}
          >
            <div 
              className="absolute inset-0 transition-transform duration-200 origin-top-left" 
              style={{ transform: `translate(${canvasOffset.x}px, ${canvasOffset.y}px) scale(${zoom})` }}
            >
              <svg className="absolute inset-0 min-w-full min-h-full pointer-events-none overflow-visible" style={{ width: '5000px', height: '5000px' }}>
                {links.map(link => {
                  const from = devices.find(d => d.id === link.from);
                  const to = devices.find(d => d.id === link.to);
                  if (!from || !to) return null;
                  const isActive = activePath.includes(link.from) && activePath.includes(link.to);
                  const isApiLink = (from.type === 'CLOUD' || to.type === 'CLOUD');
                  return (
                    <g key={link.id} className="cursor-pointer pointer-events-auto group" onClick={(e) => { e.stopPropagation(); setLinks(prev => prev.filter(l => l.id !== link.id)); }}>
                      <line 
                        x1={from.x + 40} y1={from.y + 40} x2={to.x + 40} y2={to.y + 40} 
                        stroke={isActive ? '#4f46e5' : isApiLink ? (link.isValidated ? '#4f46e5' : '#fda4af') : '#cbd5e1'} 
                        strokeWidth={isActive ? 8 : isApiLink ? 5 : 3} 
                        strokeLinecap="round" 
                        strokeDasharray={isApiLink ? "0" : "10, 8"}
                        className={isActive ? 'animate-pulse' : ''} 
                      />
                    </g>
                  );
                })}
              </svg>
              {devices.map(device => (
                <div key={device.id} onMouseDown={(e) => handleDeviceMouseDown(e, device.id)} className="absolute z-10" style={{ left: device.x, top: device.y }}>
                  <DeviceNode device={device} isSelected={selectedId === device.id} onClick={() => {}} />
                </div>
              ))}
            </div>
          </main>

          <section className="h-32 bg-white border-t z-20 flex flex-col shadow-inner">
            <div className="px-4 py-1.5 border-b bg-slate-50 flex justify-between items-center">
              <h2 className="text-[9px] font-black uppercase text-slate-500 tracking-widest">Langkah Simulasi</h2>
              <div className="text-[8px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100 uppercase tracking-tight">API URL: 10.10.10.1</div>
            </div>
            <div className="flex-1 overflow-x-auto p-3 flex gap-3 items-center scrollbar-hide">
                {[
                  { id: 'misi1', title: '1. ESP32 Auth', desc: 'Isi API Key & hubungkan ESP32 ke Cloud.', done: missionProgress.misi1 },
                  { id: 'misi2', title: '2. Sensor Pairing', desc: 'Sambungkan berbagai sensor ke ESP32.', done: missionProgress.misi2 },
                  { id: 'misi3', title: '3. Cloud Access', desc: 'Isi IP. Akses Cloud dari HP (IP: 10.10.10.1).', done: missionProgress.misi3 }
                ].map((step, idx) => (
                  <div key={step.id} className={`w-52 p-3 rounded-xl border shrink-0 transition-all ${step.done ? 'border-indigo-200 bg-indigo-50' : 'border-slate-100 bg-white shadow-sm'}`}>
                    <div className="flex gap-3 items-center">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black shrink-0 ${step.done ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-400'}`}>{step.done ? '✓' : idx + 1}</div>
                      <div>
                        <p className={`text-[10px] font-black uppercase ${step.done ? 'text-indigo-700' : 'text-slate-700'}`}>{step.title}</p>
                        <p className="text-[9px] text-slate-500 leading-tight font-medium">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </section>
        </div>

        <aside className="w-72 bg-white border-l shadow-2xl z-20 flex flex-col">
          <div className="p-4 flex-1 overflow-y-auto scrollbar-hide">
            {selectedDevice ? (
              <div className="space-y-5 animate-in slide-in-from-right-4 duration-200">
                <div className="flex justify-between items-center border-b pb-3">
                  <div className="flex flex-col">
                    <h2 className="text-[9px] font-black uppercase text-slate-400 tracking-widest">{selectedDevice.type}</h2>
                    <p className="text-xs font-bold text-slate-800">{selectedDevice.name}</p>
                  </div>
                  {selectedDevice.id !== 'cloud-master' && (
                    <button onClick={() => deleteDevice(selectedDevice.id)} className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors group" title="Hapus Alat">
                      <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  {selectedDevice.type === 'CLOUD' && (
                    <div className="bg-indigo-600 p-4 rounded-xl text-white shadow-lg">
                       <p className="text-[8px] font-black uppercase opacity-60">Secret API Auth Key</p>
                       <p className="text-lg font-mono font-black tracking-widest">{CLOUD_SECRET_KEY}</p>
                       <p className="text-[9px] mt-2 opacity-80 leading-tight">Gunakan kode ini pada kolom API Key di perangkat ESP32.</p>
                    </div>
                  )}

                  <section className="bg-slate-50 p-3 rounded-xl border border-slate-100 shadow-inner">
                    <label className="text-[9px] font-black text-slate-400 block uppercase mb-2">Konfigurasi Perangkat</label>
                    
                    {selectedDevice.type === 'CONTROLLER' && (
                      <div className="space-y-2">
                        <div>
                          <label className="text-[8px] font-bold text-indigo-500 uppercase px-1">🔑 API Key (Cloud)</label>
                          <input className="w-full px-3 py-2 bg-white border border-indigo-200 rounded-lg text-[11px] font-mono font-bold text-indigo-700 outline-none focus:ring-2 focus:ring-indigo-400" value={selectedDevice.apiKey || ''} onChange={(e) => updateDeviceConfig(selectedId!, { apiKey: e.target.value })} placeholder="Isi Kode Cloud" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[8px] font-bold text-slate-400 uppercase px-1">IP WAN</label>
                            <input className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-[11px] font-mono font-bold" value={selectedDevice.ipWan || ''} onChange={(e) => updateDeviceConfig(selectedId!, { ipWan: e.target.value })} placeholder="10.10.10.2" />
                          </div>
                          <div>
                            <label className="text-[8px] font-bold text-slate-400 uppercase px-1">IP WiFi</label>
                            <input className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-[11px] font-mono font-bold" value={selectedDevice.ip || ''} onChange={(e) => updateDeviceConfig(selectedId!, { ip: e.target.value })} placeholder="192.168.1.1" />
                          </div>
                        </div>
                      </div>
                    )}

                    {(selectedDevice.type === 'DASHBOARD' || selectedDevice.type === 'SENSOR' || selectedDevice.type === 'EXTENDER') && (
                      <div className="space-y-2">
                        <div>
                          <label className="text-[8px] font-bold text-slate-400 uppercase px-1">Alamat IP</label>
                          <input className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[11px] font-mono font-bold outline-none focus:ring-2 focus:ring-slate-300" value={selectedDevice.ip || ''} onChange={(e) => updateDeviceConfig(selectedId!, { ip: e.target.value })} placeholder="Misal: 192.168.1.20" />
                        </div>
                        <div>
                          <label className="text-[8px] font-bold text-slate-400 uppercase px-1">Gateway (ESP32)</label>
                          <input className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[11px] font-mono font-bold outline-none focus:ring-2 focus:ring-slate-300" value={selectedDevice.gateway || ''} onChange={(e) => updateDeviceConfig(selectedId!, { gateway: e.target.value })} placeholder="192.168.1.1" />
                        </div>
                      </div>
                    )}
                  </section>

                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => { setLinkingFrom(selectedId); setSelectedId(null); }} className={`py-2.5 ${selectedDevice.type === 'CONTROLLER' ? 'bg-indigo-600' : 'bg-slate-900'} text-white rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg active:scale-95 transition-transform flex items-center justify-center gap-2`}>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                      {selectedDevice.type === 'CONTROLLER' ? 'Bind API' : 'Hubungkan'}
                    </button>
                    <button onClick={() => { setLinks(prev => prev.filter(l => l.from !== selectedId && l.to !== selectedId)); }} className="py-2.5 bg-white border border-slate-200 text-slate-400 rounded-xl text-[9px] font-black uppercase active:scale-95 transition-colors hover:bg-slate-50">Putus Jalur</button>
                  </div>

                  {selectedDevice.type === 'DASHBOARD' && (
                    <section className="pt-4 border-t border-slate-100">
                      <p className="text-[8px] font-bold text-slate-400 uppercase mb-2">Simulasi HTTP Request</p>
                      <div className="flex gap-1.5">
                        <input className="flex-1 px-3 py-2.5 bg-slate-900 text-indigo-400 rounded-lg text-[10px] font-mono outline-none border border-slate-700" placeholder="IP Cloud: 10.10.10.1" value={pingIp} onChange={(e) => setPingIp(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handlePing()} />
                        <button onClick={handlePing} className="px-3 py-2.5 bg-indigo-600 text-white rounded-lg text-[9px] font-black uppercase shadow-lg shadow-indigo-100 active:scale-95 transition-all">Check</button>
                      </div>
                    </section>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 space-y-4">
                <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center text-2xl shadow-inner animate-pulse">📡</div>
                <div className="space-y-1">
                  <p className="text-slate-800 text-[10px] font-black uppercase tracking-[2px]">IoT Lab Master</p>
                  <p className="text-[10px] text-slate-400 leading-relaxed italic">Atur: Cloud ↔ ESP32 ↔ Sensors</p>
                </div>
              </div>
            )}
          </div>

          <div className="h-40 bg-slate-900 text-indigo-400 p-4 font-mono text-[10px] overflow-y-auto border-t border-slate-800">
            <p className="text-slate-500 text-[8px] font-black uppercase tracking-widest mb-2 border-b border-slate-800 pb-1">Data Console</p>
            {pingLog.length === 0 ? <p className="opacity-20 italic">Monitor siap...</p> : pingLog.map((log, i) => <div key={i} className="mb-1 leading-tight">{log}</div>)}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default App;
